import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { serviceChatAPI } from '../utils/api';
import { firestoreDb } from '../utils/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { Send, MessageSquare, X, CornerUpLeft, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import LinkPreviewCard from './LinkPreviewCard';
import ChatMessageBubble from './chat/ChatMessageBubble';
import ChatActionDrawer from './chat/ChatActionDrawer';
import MessageText from './chat/MessageText';
import { useMentionAutocomplete } from '../hooks/useMentionAutocomplete';

const PAGE_SIZE = 30;
const EVERYONE = { member_id: '_everyone', name: 'everyone', isEveryone: true };

const ServiceChatTab = ({ serviceId, participants = [], teams = [], onNewMessage, onMessagesLoaded, hideHeader = false }) => {
  const { user, firebaseReady } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);

  // Reaction state
  const [pickerMsgId, setPickerMsgId] = useState(null);
  // Reply state
  const [replyTo, setReplyTo] = useState(null);
  // Mobile long-press drawer
  const [longPressMsg, setLongPressMsg] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const initialScrollDone = useRef(false);
  const loadedIdsRef = useRef(new Set());
  const inputRef = useRef(null);
  const pendingMsgIdRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressStartPosRef = useRef(null);
  const [listenerGate, setListenerGate] = useState(null);

  // @mention autocomplete via shared hook — includes @everyone, mentionable teams, and all named participants
  const mentionParticipants = useMemo(
    () => [EVERYONE, ...teams, ...participants.filter(p => p.name)],
    [participants, teams]
  );

  const {
    mentionOpen,
    setMentionOpen,
    mentionIdx,
    filteredParticipants,
    selectMention,
    onInputChange,
    onKeyDown: handleKeyDown,
    onInputBlur: handleInputBlur,
  } = useMentionAutocomplete({ text, setText, participants: mentionParticipants, inputRef });

  // Close reaction picker on any outside click
  useEffect(() => {
    if (!pickerMsgId) return;
    const close = () => setPickerMsgId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [pickerMsgId]);

  const loadMessages = useCallback(async () => {
    try {
      const res = await serviceChatAPI.getMessages(serviceId, 0, PAGE_SIZE);
      const { items, total, has_more, msg_count } = res.data;
      setMessages(items);
      setTotalMessages(total);
      setHasOlder(has_more);
      loadedIdsRef.current = new Set(items.map(m => m.message_id));
      initialScrollDone.current = false;
      if (onMessagesLoaded) onMessagesLoaded(msg_count ?? total);
      const startTs = items.length > 0
        ? items[items.length - 1].created_at
        : new Date().toISOString();
      setListenerGate({ serviceId, startTs });
    } catch { /* ignore */ }
  }, [serviceId, onMessagesLoaded]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadMessages();
      setLoading(false);
    };
    init();
  }, [loadMessages]);

  // Firestore listener for NEW messages
  useEffect(() => {
    if (!firebaseReady || !listenerGate || listenerGate.serviceId !== serviceId) return;
    const chatId = `svc_${serviceId}`;
    const q = query(
      collection(firestoreDb, 'messages'),
      where('chat_id', '==', chatId),
      where('created_at', '>', listenerGate.startTs)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const msg = change.doc.data();
        if (change.type === 'added' && !loadedIdsRef.current.has(msg.message_id)) {
          loadedIdsRef.current.add(msg.message_id);
          pendingMsgIdRef.current = null;
          setMessages(prev => {
            if (prev.some(m => m.message_id === msg.message_id)) return prev;
            const filtered = msg.sender_id === user?.user_id
              ? prev.filter(m => !(m.pending && m.sender_id === user?.user_id))
              : prev;
            const updated = [...filtered, msg];
            updated.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
            return updated;
          });
          setTotalMessages(prev => prev + 1);
          if (msg.sender_id !== user?.user_id && onNewMessage) onNewMessage(msg);
        }
      });
    }, (err) => console.warn('[Firestore] Service chat listener error:', err));
    return () => unsubscribe();
  }, [serviceId, firebaseReady, listenerGate, user?.user_id, onNewMessage]);

  // Second Firestore listener — syncs reaction/edit/delete modifications
  useEffect(() => {
    if (!firebaseReady || !listenerGate || listenerGate.serviceId !== serviceId) return;
    const chatId = `svc_${serviceId}`;
    const q = query(
      collection(firestoreDb, 'messages'),
      where('chat_id', '==', chatId),
      where('created_at', '>=', listenerGate.startTs)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'modified') {
          const updated = change.doc.data();
          setMessages(prev => prev.map(m =>
            m.message_id === updated.message_id
              ? { ...m, reactions: updated.reactions, link_preview: updated.link_preview, deleted: updated.deleted }
              : m
          ));
        }
      });
    }, () => {});
    return () => unsubscribe();
  }, [serviceId, firebaseReady, listenerGate]);

  // Polling fallback
  useEffect(() => {
    if (firebaseReady) return;
    const interval = setInterval(loadMessages, 4000);
    return () => clearInterval(interval);
  }, [firebaseReady, loadMessages]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!loading && messages.length > 0 && !initialScrollDone.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      initialScrollDone.current = true;
    }
  }, [loading, messages.length]);

  // Scroll to bottom on new messages if near bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !initialScrollDone.current) return;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    if (isNearBottom && container) container.scrollTop = container.scrollHeight;
  }, [messages]);

  const loadOlderMessages = useCallback(async () => {
    if (loadingOlder || !hasOlder) return;
    setLoadingOlder(true);
    const container = messagesContainerRef.current;
    const prevScrollHeight = container?.scrollHeight || 0;
    try {
      const res = await serviceChatAPI.getMessages(serviceId, messages.length, PAGE_SIZE);
      const { items, has_more } = res.data;
      if (items.length > 0) {
        items.forEach(m => loadedIdsRef.current.add(m.message_id));
        setMessages(prev => [...items, ...prev]);
        setHasOlder(has_more);
        requestAnimationFrame(() => {
          if (container) container.scrollTop = container.scrollHeight - prevScrollHeight;
        });
      } else setHasOlder(false);
    } catch { /* ignore */ }
    finally { setLoadingOlder(false); }
  }, [serviceId, loadingOlder, hasOlder, messages.length]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el || loadingOlder || !hasOlder) return;
    if (el.scrollTop < 60) loadOlderMessages();
  }, [loadingOlder, hasOlder, loadOlderMessages]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const isAdmin = ['root', 'org_admin', 'administrator'].includes(user?.permission_level);
  const memberMap = useMemo(() => {
    const map = {};
    messages.forEach(m => {
      if (m.sender_id && m.sender_name && !map[m.sender_id]) {
        map[m.sender_id] = { name: m.sender_name, picture: null };
      }
    });
    participants.forEach(p => {
      if (p.member_id) {
        if (map[p.member_id]) map[p.member_id].picture = p.picture || null;
        else map[p.member_id] = { name: p.name, picture: p.picture || null };
      }
    });
    // Always include current user with their actual picture
    if (user?.user_id) {
      if (map[user.user_id]) map[user.user_id].picture = user.picture || null;
      else map[user.user_id] = { name: user.name || user.email || 'Me', picture: user.picture || null };
    }
    return map;
  }, [messages, participants, user?.user_id, user?.picture, user?.name, user?.email]);

  /* ---- Delete handler ---- */
  const handleDelete = useCallback(async (msgId) => {
    setMessages(prev => prev.map(m =>
      m.message_id === msgId ? { ...m, deleted: true } : m
    ));
    try {
      await serviceChatAPI.deleteMessage(serviceId, msgId);
    } catch {
      toast.error('Failed to delete message');
      setMessages(prev => prev.map(m =>
        m.message_id === msgId ? { ...m, deleted: false } : m
      ));
    }
  }, [serviceId]);

  /* ---- Reaction handler ---- */
  const handleReact = useCallback(async (msgId, emoji) => {
    setPickerMsgId(null);
    setMessages(prev => prev.map(m => {
      if (m.message_id !== msgId) return m;
      const reactions = { ...(m.reactions || {}) };
      const users = [...(reactions[emoji] || [])];
      const idx = users.indexOf(user?.user_id);
      if (idx >= 0) users.splice(idx, 1);
      else users.push(user?.user_id);
      reactions[emoji] = users;
      return { ...m, reactions };
    }));
    try {
      await serviceChatAPI.react(serviceId, msgId, emoji);
    } catch {
      toast.error('Failed to save reaction');
    }
  }, [serviceId, user?.user_id]);

  /* ---- Reply handler ---- */
  const handleReply = useCallback((snapshot) => {
    setReplyTo(snapshot);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  /* ---- Long-press (mobile bottom drawer) ---- */
  const startLongPress = useCallback((msg, e) => {
    const touch = e.touches?.[0];
    longPressStartPosRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    longPressTimerRef.current = setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      setLongPressMsg({ ...msg, _touchY: touch?.clientY ?? null });
      setPickerMsgId(null);
    }, 500);
  }, []);

  const cancelLongPress = useCallback((e) => {
    if (e?.type === 'touchmove' && longPressStartPosRef.current) {
      const touch = e.touches?.[0];
      if (touch) {
        const dx = Math.abs(touch.clientX - longPressStartPosRef.current.x);
        const dy = Math.abs(touch.clientY - longPressStartPosRef.current.y);
        if (dx < 8 && dy < 8) return;
      }
    }
    clearTimeout(longPressTimerRef.current);
  }, []);

  /* ---- Send ---- */
  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending || mentionOpen) return;
    const trimmed = text.trim();
    setSending(true);
    const currentReplyTo = replyTo;
    setReplyTo(null);

    const tempId = `temp-${Date.now()}`;
    pendingMsgIdRef.current = tempId;
    const tempMsg = {
      message_id: tempId,
      chat_id: `svc_${serviceId}`,
      sender_id: user?.user_id,
      sender_name: user?.name || user?.email,
      text: trimmed,
      created_at: new Date().toISOString(),
      pending: true,
      ...(currentReplyTo ? { reply_to: currentReplyTo } : {}),
    };
    setMessages(prev => [...prev, tempMsg]);
    setText('');

    try {
      const res = await serviceChatAPI.sendMessage(serviceId, trimmed, currentReplyTo?.message_id || null);
      const realMsg = res.data;
      loadedIdsRef.current.add(realMsg.message_id);
      pendingMsgIdRef.current = null;
      setMessages(prev => {
        const withoutTemp = prev.filter(m => m.message_id !== tempId);
        if (withoutTemp.some(m => m.message_id === realMsg.message_id)) return withoutTemp;
        const updated = [...withoutTemp, realMsg];
        updated.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        return updated;
      });
    } catch {
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      pendingMsgIdRef.current = null;
      setText(trimmed);
      setReplyTo(currentReplyTo);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (ts) => {
    try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); }
    catch { return ''; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700" />
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col flex-1 min-h-0 ${hideHeader ? 'rounded-none' : 'rounded-xl'}`} data-testid="service-chat-container">
      {/* Header */}
      {!hideHeader && (
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-green-600" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Service Discussion</span>
          <span className="ml-auto text-xs text-slate-400">{totalMessages} message{totalMessages !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 space-y-3 bg-slate-50 dark:bg-slate-800/50"
        ref={messagesContainerRef}
        data-testid="service-chat-messages"
        onClick={() => setPickerMsgId(null)}
      >
        {loadingOlder && (
          <div className="py-2 text-center">
            <div className="animate-spin inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full" />
          </div>
        )}
        {!hasOlder && messages.length > 0 && totalMessages > PAGE_SIZE && (
          <p className="text-center text-[10px] text-slate-400 py-1">Beginning of discussion</p>
        )}
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-8">
            <MessageSquare className="w-10 h-10 text-slate-200 mb-3" />
            <p className="text-sm text-slate-400">No messages yet.</p>
            <p className="text-xs text-slate-400 mt-1">Start the discussion!</p>
          </div>
        )}
        {messages.map(msg => {
          const isMe = msg.sender_id === user?.user_id;
          const canDelete = !msg.deleted && (isMe || isAdmin);
          return (
            <ChatMessageBubble
              key={msg.message_id}
              msg={msg}
              isMe={isMe}
              canDelete={canDelete}
              userId={user?.user_id}
              pickerMsgId={pickerMsgId}
              onSetPickerMsgId={setPickerMsgId}
              onReply={handleReply}
              onReact={handleReact}
              onDelete={handleDelete}
              onLongPressStart={startLongPress}
              onLongPressCancel={cancelLongPress}
              memberMap={memberMap}
              testIdPrefix="svc-msg"
              maxWidth="72%"
              senderLabel={
                !isMe ? (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1 ml-1 font-medium">{msg.sender_name}</p>
                ) : null
              }
              leftDecorator={
                !isMe ? (
                  <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 flex items-center justify-center text-xs font-semibold flex-shrink-0 mb-0.5">
                    {(msg.sender_name || '?')[0].toUpperCase()}
                  </div>
                ) : null
              }
              rightDecorator={
                isMe ? (
                  <div className="w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0 mb-0.5">
                    {(user?.name || user?.email || '?')[0].toUpperCase()}
                  </div>
                ) : null
              }
              bubbleContent={
                <>
                  <p className="text-sm whitespace-pre-wrap break-words">
                    <MessageText text={msg.text} participants={[EVERYONE, ...teams, ...participants]} />
                  </p>
                  <p className={`text-[10px] mt-0.5 ${isMe ? 'text-right text-green-200' : 'text-slate-400 dark:text-slate-500'}`}>
                    {msg.pending
                      ? <span className="flex items-center gap-0.5"><span className="w-2.5 h-2.5 border border-current rounded-full border-t-transparent animate-spin inline-block" /> Sending…</span>
                      : formatTime(msg.created_at)
                    }
                  </p>
                </>
              }
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Mobile long-press bottom drawer */}
      {longPressMsg && (
        <ChatActionDrawer
          msg={longPressMsg}
          userId={user?.user_id}
          touchY={longPressMsg._touchY}
          senderDisplayName={longPressMsg.sender_name || 'Unknown'}
          onReact={handleReact}
          onReply={(snapshot) => {
            setReplyTo(snapshot);
            setLongPressMsg(null);
            setTimeout(() => inputRef.current?.focus(), 100);
          }}
          onDelete={handleDelete}
          onClose={() => setLongPressMsg(null)}
        />
      )}

      {/* Input area with mention dropdown */}
      <div className="relative border-t border-slate-200 dark:border-slate-700">
        {/* Reply preview bar */}
        {replyTo && (
          <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800 flex items-center gap-2">
            <CornerUpLeft className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-green-700 dark:text-green-400 truncate">
                Replying to {replyTo.sender_name}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{replyTo.text}</p>
            </div>
            <button
              onClick={() => setReplyTo(null)}
              data-testid="cancel-reply-btn"
              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Mention dropdown */}
        {mentionOpen && filteredParticipants.length > 0 && (
          <div
            data-testid="mention-dropdown"
            className="absolute bottom-full left-4 right-4 mb-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-10"
          >
            <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
              <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Mention someone</span>
              <span className="text-[10px] text-slate-400">↑↓ navigate · Enter select · Esc close</span>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {filteredParticipants.map((p, i) => (
                <button
                  key={p.member_id}
                  onMouseDown={(e) => { e.preventDefault(); selectMention(p.name); }}
                  data-testid={`mention-option-${p.member_id}`}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors ${
                    i === mentionIdx ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300' : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${
                    p.isEveryone
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                      : p.isTeam
                        ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400'
                        : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400'
                  }`}>
                    {p.isEveryone ? '@' : p.isTeam ? <Users className="w-3 h-3" /> : p.name[0].toUpperCase()}
                  </div>
                  <span className="text-sm font-medium">{p.isEveryone ? 'everyone' : p.name}</span>
                  {p.isEveryone && <span className="text-xs text-blue-500 dark:text-blue-400 ml-auto">Notify all</span>}
                  {p.isTeam && <span className="text-xs text-purple-500 dark:text-purple-400 ml-auto">{p.memberCount} member{p.memberCount === 1 ? '' : 's'}</span>}
                  {!p.isEveryone && !p.isTeam && <span className="text-xs text-green-600 dark:text-green-400 font-medium ml-auto">@{p.name}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSend} className="px-4 py-3 bg-white dark:bg-slate-800 flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={text}
              onChange={onInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleInputBlur}
              placeholder="Type a message… use @ to mention someone"
              data-testid="svc-chat-input"
              className="w-full px-4 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            />
          </div>
          <button
            type="submit"
            disabled={!text.trim() || sending}
            data-testid="svc-chat-send-btn"
            className="p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ServiceChatTab;
